import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { Link } from "react-router-dom";
import {
  Float,
  Environment,
  useGLTF,
  OrbitControls,
} from "@react-three/drei";
import { useRef, Suspense } from "react";

/* ===================== RESPONSIVE VALUES ===================== */
function useResponsiveValues() {
  const { viewport } = useThree();
  const isMobile = viewport.width < 6;

  return {
    isMobile,
    modelScale: isMobile ? 12 : 16,
    modelY: isMobile ? -1.8 : -2.6,
    cameraPos: isMobile ? [0.6, 3.2, 20] : [1.3, 4.1, 16.2],
    ringInner: isMobile ? 3.5 : 4.5,
    ringOuter: isMobile ? 4.1 : 5.1,
    floatIntensity: isMobile ? 0.25 : 0.38,
    rotateSpeed: isMobile ? 0.06 : 0.1,
  };
}

/* ===================== 3D MODEL ===================== */
function LaptopModel() {
  const ref = useRef();
  const { scene } = useGLTF("/computerModel.glb");
  const { modelScale, modelY, rotateSpeed } = useResponsiveValues();

  useFrame((_, delta) => {
    if (ref.current) {
      ref.current.rotation.y += delta * rotateSpeed;
    }
  });

  return (
    <primitive
      ref={ref}
      object={scene}
      scale={modelScale}
      position={[0, modelY, 0]}
      rotation={[0.08, Math.PI * 0.9, 0]}
    />
  );
}

/* ===================== BASE RING ===================== */
function BaseRing() {
  const { ringInner, ringOuter } = useResponsiveValues();

  return (
    <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -2.3, 0]}>
      <ringGeometry args={[ringInner, ringOuter, 128]} />
      <meshStandardMaterial
        color="#d70d40"
        transparent
        opacity={0.18}
      />
    </mesh>
  );
}

/* ===================== RESPONSIVE CAMERA ===================== */
function ResponsiveCamera() {
  const { camera } = useThree();
  const { cameraPos } = useResponsiveValues();

  camera.position.set(...cameraPos);
  camera.updateProjectionMatrix();
  return null;
}

/* ===================== HERO SECTION ===================== */
export default function HeroSection() {
  return (
    <section className="w-full bg-white flex items-center overflow-hidden">
      <div
        className="
          max-w-7xl mx-auto w-full 
          px-4 sm:px-6
          py-10 md:py-20
          grid grid-cols-1 md:grid-cols-2
          gap-10 md:gap-16
          items-center
        "
      >

        {/* ================= LEFT CONTENT ================= */}
        <div className="space-y-6 text-left">
          {/* TAG */}
          <span className="inline-block px-4 py-2 rounded-full text-xs tracking-widest bg-red-50 border border-red-200 text-[#d70d40] font-semibold">
            ANY BRAND • ANY PROBLEM
          </span>

          {/* HEADLINE */}
          <h1 className="text-4xl md:text-5xl xl:text-6xl font-bold leading-tight text-[#1f2a7a]">
            Trusted Laptop{" "}
            <span className="text-[#d70d40]">Repair & Refurbish</span>
            <br />
            Experts
          </h1>

          {/* DESCRIPTION */}
          <p className="max-w-xl text-gray-600 text-lg leading-relaxed">
            We repair, upgrade, and refurbish laptops of all brands.
            Certified engineers, genuine parts, and complete peace of mind.
          </p>

          {/* CTA */}
        <div className="flex gap-3 flex-wrap">
  <Link
    to="/contact"
    className="
      no-underline p-8
      bg-gradient-to-r from-[#1F3C88] to-[#3f5bdc]
      hover:from-[#162e6f] hover:to-[#2f47c4]
      text-white py-2 rounded-xl text-sm font-semibold
      transition flex items-center justify-center gap-2
      group-hover:scale-[1.02]
    "
  >
    Contact Now
  </Link>

  <Link
    to="/services"
    className="
      no-underline  p-8
      bg-gradient-to-r from-[#1F3C88] to-[#3f5bdc]
      hover:from-[#162e6f] hover:to-[#2f47c4]
      text-white py-2 rounded-xl text-sm font-semibold
      transition flex items-center justify-center gap-2
      group-hover:scale-[1.02]
    "
  >
    View Services
  </Link>
</div>

        </div>

        {/* ================= RIGHT 3D ================= */}
        <div className="relative w-full aspect-[16/11] sm:aspect-[16/11] md:aspect-[16/10]">
          <Canvas camera={{ fov: 36 }}>
            <Suspense fallback={null}>
              <ResponsiveCamera />

              {/* CONTROLS */}
              <OrbitControls
                enableZoom={false}
                enablePan={false}
                autoRotate
                autoRotateSpeed={0.4}
              />

              {/* LIGHTING */}
              <directionalLight position={[9, 11, 7]} intensity={2.1} />
              <directionalLight position={[-7, 4, -7]} intensity={0.75} />
              <directionalLight position={[0, 3, -11]} intensity={1} />
              <ambientLight intensity={0.45} />

              <Environment preset="city" />

              <BaseRing />

              <Float speed={1} floatIntensity={0.35}>
                <LaptopModel />
              </Float>
            </Suspense>
          </Canvas>
        </div>

      </div>
    </section>
  );
}


useGLTF.preload("/computerModel.glb");
